'use client';
import React, { useEffect, useRef, useState } from 'react';

const LOGOS = [
  { name: 'BBC News', abbr: 'BBC' },
  { name: 'The Guardian', abbr: 'GRD' },
  { name: 'Wired', abbr: 'WRD' },
  { name: 'TechCrunch', abbr: 'TCH' },
  { name: 'Vice', abbr: 'VCE' },
  { name: 'Mashable', abbr: 'MSH' },
  { name: 'Fast Company', abbr: 'FCO' },
  { name: 'Hacker News', abbr: 'HKN' },
  { name: 'Product Hunt', abbr: 'PHT' },
  { name: 'Reddit', abbr: 'RDT' },
];

const COUNTERS = [
  { value: 47382, label: 'Luck incidents logged', suffix: '' },
  { value: 31400, label: 'Unique visitors posted', suffix: '+' },
  { value: 140, label: 'Countries represented', suffix: '' },
  { value: 98.7, label: 'Uptime', suffix: '%' },
];

function AnimatedCounter({ target, suffix }: { target: number; suffix: string }) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const start = performance.now();
          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setVal(target * eased);
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  const display = target < 100
    ? val.toFixed(1)
    : Math.round(val).toLocaleString();

  return (
    <span ref={ref} className="tabular-nums">
      {display}{suffix}
    </span>
  );
}

export default function SocialProofSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative overflow-hidden">
      <div className="section-divider mb-12" />

      {/* Counters */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {COUNTERS.map((c, i) => (
            <div
              key={c.label}
              className={`reveal-up delay-${(i + 1) * 100} text-center p-6 rounded-2xl`}
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="text-4xl lg:text-5xl font-display font-700 text-mint mb-2">
                <AnimatedCounter target={c.value} suffix={c.suffix} />
              </div>
              <p className="text-sm text-white/40 font-body">{c.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Logo marquee */}
      <div className="reveal-up mb-6">
        <p className="text-center text-xs font-display font-600 text-white/20 uppercase tracking-widest mb-8">
          As seen and shared across
        </p>
        <div className="overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #0D0F1C, transparent)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #0D0F1C, transparent)' }} />

          <div className="marquee-track">
            {[...LOGOS, ...LOGOS].map((logo, i) => (
              <div
                key={`${logo.name}-${i}`}
                className="flex items-center gap-2 mx-10 opacity-40 hover:opacity-80 transition-opacity duration-300 cursor-default"
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center font-mono text-xs font-700"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#F0F2FF' }}
                >
                  {logo.abbr.slice(0, 2)}
                </div>
                <span className="text-sm font-display font-500 text-iwhite whitespace-nowrap">{logo.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-divider mt-12" />
    </section>
  );
}